import ApplicationComponent from "legacy/ApplicationComponent"
import FormGroup from "legacy/FormGroup"
import { inchesToFeet } from "legacy/helpers.es6"
import _ from "lodash"
import PropTypes from "prop-types"
import React from "react"

import ScrollShadow from "src/components/ScrollShadow"

import { highlightSpace } from "./highlightSpace"

class AssignmentSearchForm extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.state = {
      assignmentSearch: "",
      filteredResults: [],
      filteredSpaces: [],
    }
    this.handleSearch = this.handleSearch.bind(this)
    this.handleWindowClick = this.handleWindowClick.bind(this)
    this.searchResultsRef = null
    this.setSearchResultsRef = (element) => {
      this.searchResultsRef = element
    }
  }

  componentDidMount() {
    window.addEventListener("click", this.handleWindowClick)
  }

  componentWillUnmount() {
    window.removeEventListener("click", this.handleWindowClick)
  }

  handleWindowClick(event) {
    if (
      this.searchResultsRef &&
      !this.searchResultsRef.contains(event.target)
    ) {
      this.setState({
        assignmentSearch: "",
        filteredResults: [],
        filteredSpaces: [],
      })
      if (this.isMarinaMapView()) {
        this.props.setGlobalState({
          searchResults: [],
          highlightedSpace: null,
        })
      }
    }
  }

  isMarinaMapView() {
    return this.props.viewType.includes("map")
  }

  handleMouseEnter(object) {
    if (this.isMarinaMapView()) {
      this.props.setGlobalState({ highlightedSpace: object })
    }
  }

  normalizeCode(value) {
    return value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()
  }

  compactCode(value) {
    return value.replace(/([A-Z])0+(\d)/g, "$1$2")
  }

  matchValue(object, value, params) {
    return params.some((key) => {
      const lowerCaseValue = value.toLowerCase()
      const lowerCaseFieldValue = (_.get(object, key) || "").toLowerCase()
      return lowerCaseFieldValue.includes(lowerCaseValue)
    })
  }

  matchSpaceName(spaceName, searchTerm) {
    if (!spaceName || !searchTerm) return false
    const normalizedName = this.normalizeCode(spaceName)
    const normalizedTerm = this.normalizeCode(searchTerm)
    if (!normalizedTerm) return false
    const compactedName = this.compactCode(normalizedName)
    const compactedTerm = this.compactCode(normalizedTerm)
    return (
      normalizedName.includes(normalizedTerm) ||
      compactedName.includes(compactedTerm)
    )
  }

  handleSearch(event) {
    const value = event.target.value
    let filteredResults = []
    const filteredSpaces = this.props.searchFormData.filter((space) => {
      if (this.matchSpaceName(space.name, value)) {
        let assignmentsArray = space.assignments || []
        if (this.props.viewType.includes("map")) {
          assignmentsArray = assignmentsArray.filter(
            (assignment) => !assignment.checked_out_at
          )
        }
        const hasContent =
          assignmentsArray.length > 0 ||
          (space.placeholders && space.placeholders.length > 0) ||
          (space.spaceNotes && space.spaceNotes.length > 0)
        if (assignmentsArray.length > 0) {
          filteredResults = [...filteredResults, ...assignmentsArray]
        }
        if (space.placeholders && space.placeholders.length > 0) {
          filteredResults = [...filteredResults, ...space.placeholders]
        }
        if (space.spaceNotes && space.spaceNotes.length > 0) {
          filteredResults = [...filteredResults, ...space.spaceNotes]
        }
        if (!hasContent) {
          filteredResults = [
            ...filteredResults,
            { type: "empty_space", space_id: space.id, name: space.name },
          ]
        }
        return true
      }
      if (space.assignments.length > 0) {
        let assignmentsArray = space.assignments
        if (this.props.viewType.includes("map")) {
          assignmentsArray = space.assignments.filter(
            (assignment) => !assignment.checked_out_at
          )
        }
        const assignments = assignmentsArray.filter((assignment) => {
          return this.matchValue(assignment, value, [
            "boat_name",
            "contact_name",
            "hull_identification",
          ])
        })
        if (assignments.length > 0) {
          filteredResults = [...filteredResults, ...assignments]
          return true
        }
      }
      if (space.placeholders && space.placeholders.length > 0) {
        const placeholders = space.placeholders.filter((placeholder) => {
          return this.matchValue(placeholder, value, ["note"])
        })
        if (placeholders.length > 0) {
          filteredResults = [...filteredResults, ...placeholders]
          return true
        }
      }
      if (space.spaceNotes && space.spaceNotes.length > 0) {
        const spaceNotes = space.spaceNotes.filter((spaceNote) => {
          return this.matchValue(spaceNote, value, ["note"])
        })
        if (spaceNotes.length > 0) {
          filteredResults = [...filteredResults, ...spaceNotes]
          return true
        }
      }
      return false
    })
    if (value) {
      this.setState({
        filteredSpaces,
        filteredResults,
      })
      if (this.isMarinaMapView()) {
        this.props.setGlobalState({ searchResults: filteredSpaces })
      }
    } else {
      this.setState({
        filteredSpaces: [],
        filteredResults: [],
      })
      if (this.isMarinaMapView()) {
        this.props.setGlobalState({ searchResults: [] })
      }
    }
    this.setState({ assignmentSearch: value })
  }

  scrollToSpace(spaceId) {
    const el = document.querySelector(`[data-space-id="${spaceId}"]`)
    if (!el) return

    el.scrollIntoView({ behavior: "smooth", block: "center" })
    highlightSpace(spaceId)
  }

  handleSearchItemClick(space, object) {
    if (this.isMarinaMapView()) {
      if (object.type !== "empty_space") {
        this.props.handleMarinaMapSpaceClick(space, [object])
      }
    } else {
      const spaceId = space?.id || object.space_id
      const wasExpanded =
        this.props.expandSpaceGroupForSpace?.(spaceId) || false
      if (wasExpanded) {
        requestAnimationFrame(() => this.scrollToSpace(spaceId))
      } else {
        this.scrollToSpace(spaceId)
      }
      if (object.type === "assignment") {
        this.props.loadReservationForSidebar(object.reservation_id, space, {
          skipScroll: true,
        })
      } else if (object.type === "placeholder") {
        this.props.setPlaceholder(object, space, { skipScroll: true })
      } else if (this.props.closeSidebar) {
        this.props.closeSidebar()
      }
    }
    this.setState({
      filteredSpaces: [],
      filteredResults: [],
      assignmentSearch: "",
    })
    if (this.isMarinaMapView()) {
      this.props.setGlobalState({
        searchResults: [],
        highlightedSpace: null,
      })
    }
  }

  renderAssignment(assignment, index) {
    const spaceWithAssignment = this.state.filteredSpaces.find((space) =>
      space.assignments.includes(assignment)
    )
    return (
      <div
        onMouseEnter={() => this.handleMouseEnter(spaceWithAssignment)}
        onMouseLeave={() =>
          this.isMarinaMapView() &&
          this.props.setGlobalState({ highlightedSpace: null })
        }
        key={index}
        className="cursor-pointer px-4 py-2 hover:bg-blue-50"
        onClick={() =>
          this.handleSearchItemClick(spaceWithAssignment, assignment)
        }
      >
        <div className="flex w-full items-baseline justify-between">
          <span className="text-lg">{assignment.boat_name}</span>
          <span className="ml-2 shrink-0 text-sm text-gray-500">
            {spaceWithAssignment?.name}
          </span>
        </div>
        <div className="text-muted flex w-full justify-between">
          <span className="truncate">{assignment.contact_name}</span>
          <span className="ml-2 shrink-0">
            Res #{assignment.reservation_id}
          </span>
        </div>
      </div>
    )
  }

  renderPlaceholder(placeholder, index) {
    const spaceWithPlaceholder = this.state.filteredSpaces.find(
      (space) => space.placeholders && space.placeholders.includes(placeholder)
    )
    return (
      <div
        onMouseEnter={() => this.handleMouseEnter(spaceWithPlaceholder)}
        onMouseLeave={() =>
          this.isMarinaMapView() &&
          this.props.setGlobalState({ highlightedSpace: null })
        }
        key={index}
        className="cursor-pointer px-4 py-2 hover:bg-blue-50"
        onClick={() =>
          this.handleSearchItemClick(spaceWithPlaceholder, placeholder)
        }
      >
        <div className="flex w-full items-baseline justify-between">
          <span className="text-lg">
            <i className="icon icon-thumb-tack" /> Placeholder
          </span>
          <span className="ml-2 shrink-0 text-sm text-gray-500">
            {spaceWithPlaceholder?.name}
          </span>
        </div>
        <div className="text-muted flex w-full justify-between">
          <span className="truncate">Note: {placeholder.note}</span>
          <span className="ml-2 shrink-0">
            LOA: {inchesToFeet(placeholder.length_overall)} ft
          </span>
        </div>
      </div>
    )
  }

  renderSpaceNote(spaceNote, index) {
    const spaceWithSpaceNote = this.state.filteredSpaces.find(
      (space) => space.spaceNotes && space.spaceNotes.includes(spaceNote)
    )
    return (
      <div
        key={index}
        className="cursor-pointer px-4 py-2 hover:bg-blue-50"
        onClick={() =>
          this.handleSearchItemClick(spaceWithSpaceNote, spaceNote)
        }
      >
        <div className="flex w-full items-baseline justify-between">
          <span className="text-lg">
            <i className="icon icon-clipboard-pencil" /> Space Note
          </span>
          <span className="ml-2 shrink-0 text-sm text-gray-500">
            {spaceWithSpaceNote?.name}
          </span>
        </div>
        <div className="text-muted">
          <span className="truncate">Note: {spaceNote.note}</span>
        </div>
      </div>
    )
  }

  renderEmptySpace(emptySpace, index) {
    const space = this.state.filteredSpaces.find(
      (s) => s.id === emptySpace.space_id
    )
    return (
      <div
        onMouseEnter={() => this.handleMouseEnter(space)}
        onMouseLeave={() =>
          this.isMarinaMapView() &&
          this.props.setGlobalState({ highlightedSpace: null })
        }
        key={index}
        className="cursor-pointer px-4 py-2 hover:bg-blue-50"
        onClick={() => this.handleSearchItemClick(space, emptySpace)}
      >
        <div className="flex w-full items-baseline justify-between">
          <span className="text-lg text-gray-400">Open slip</span>
          <span className="ml-2 shrink-0 text-sm text-gray-500">
            {emptySpace.name}
          </span>
        </div>
      </div>
    )
  }

  renderSearchResults() {
    const { filteredResults, assignmentSearch } = this.state
    const hasResults = filteredResults.length > 0
    if (hasResults) {
      return (
        <div className="absolute z-50 mt-12 w-full rounded-b bg-white shadow-md">
          <ScrollShadow
            direction="vertical"
            scrollContainerClassName="max-h-[60vh] py-2"
          >
            {filteredResults.map((result, index) => {
              if (result.type === "assignment") {
                return this.renderAssignment(result, index)
              } else if (result.type === "placeholder") {
                return this.renderPlaceholder(result, index)
              } else if (result.type === "empty_space") {
                return this.renderEmptySpace(result, index)
              } else {
                return this.renderSpaceNote(result, index)
              }
            })}
          </ScrollShadow>
        </div>
      )
    } else if (assignmentSearch) {
      return (
        <div className="absolute z-50 mt-12 w-full rounded-b bg-white p-6 text-center shadow-md">
          No assignments found matching &quot;{assignmentSearch}&quot;
        </div>
      )
    }
  }

  render() {
    return (
      <div className="relative flex h-10 w-full" ref={this.setSearchResultsRef}>
        <FormGroup
          inputType="text-icon"
          iconText="icon-search-fa"
          name="assignmentSearch"
          onChange={this.handleSearch}
          value={this.state.assignmentSearch}
          placeholder="Search by space, boat name, HIN, captain, or note"
          className="w-full"
        />
        {this.renderSearchResults()}
      </div>
    )
  }
}

AssignmentSearchForm.propTypes = {
  closeSidebar: PropTypes.func,
  expandSpaceGroupForSpace: PropTypes.func,
  handleMarinaMapSpaceClick: PropTypes.func,
  loadReservationForSidebar: PropTypes.func.isRequired,
  searchFormData: PropTypes.array.isRequired,
  setGlobalState: PropTypes.func,
  setPlaceholder: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
}

export default AssignmentSearchForm
